import type { BlockType, BlockContent, BlockSettings } from '@mawkaa/types';

export type BlockCategory = 'layout' | 'content' | 'media' | 'business' | 'forms' | 'utility';

export interface BlockDefinition {
  type: BlockType;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  category: BlockCategory;
  icon: string;
  defaultContent: BlockContent;
  defaultSettings: BlockSettings;
  premium?: boolean;
}

export const blockRegistry: Record<BlockType, BlockDefinition> = {
  header: {
    type: 'header',
    name: 'Header',
    nameAr: 'الرأس',
    description: 'Navigation header with logo and menu',
    descriptionAr: 'رأس الصفحة مع الشعار والقائمة',
    category: 'layout',
    icon: 'layout-navbar',
    defaultContent: {
      logoText: 'Mon Site',
      navigation: [
        { label: 'Accueil', link: '/' },
        { label: 'Services', link: '/services' },
        { label: 'Contact', link: '/contact' },
      ],
      showCta: true,
      ctaText: 'Contactez-nous',
      ctaLink: '/contact',
      style: 'simple',
    },
    defaultSettings: {
      backgroundColor: '#ffffff',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  hero: {
    type: 'hero',
    name: 'Hero',
    nameAr: 'البطل',
    description: 'Large hero section with title and call-to-action',
    descriptionAr: 'قسم رئيسي كبير مع عنوان ودعوة للعمل',
    category: 'layout',
    icon: 'layout-dashboard',
    defaultContent: {
      title: 'Bienvenue sur notre site',
      subtitle: 'Votre partenaire de confiance',
      description: 'Découvrez nos services professionnels adaptés à vos besoins.',
      overlay: true,
      overlayOpacity: 0.5,
      ctaButtons: [
        { text: 'En savoir plus', link: '/about', variant: 'primary' },
        { text: 'Contactez-nous', link: '/contact', variant: 'outline' },
      ],
      style: 'centered',
      height: 'large',
    },
    defaultSettings: {
      paddingTop: 0,
      paddingBottom: 0,
      fullWidth: true,
    },
  },
  text: {
    type: 'text',
    name: 'Text',
    nameAr: 'نص',
    description: 'Rich text content block',
    descriptionAr: 'كتلة محتوى نصي غني',
    category: 'content',
    icon: 'type',
    defaultContent: {
      content: '<p>Ajoutez votre contenu ici...</p>',
      alignment: 'left',
    },
    defaultSettings: {
      paddingTop: 40,
      paddingBottom: 40,
    },
  },
  image: {
    type: 'image',
    name: 'Image',
    nameAr: 'صورة',
    description: 'Single image with optional caption',
    descriptionAr: 'صورة واحدة مع تعليق اختياري',
    category: 'media',
    icon: 'image',
    defaultContent: {
      src: '',
      alt: 'Image description',
      aspectRatio: 'auto',
    },
    defaultSettings: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
  gallery: {
    type: 'gallery',
    name: 'Gallery',
    nameAr: 'معرض',
    description: 'Image gallery with multiple photos',
    descriptionAr: 'معرض صور متعدد',
    category: 'media',
    icon: 'images',
    defaultContent: {
      images: [],
      columns: 3,
      style: 'grid',
      lightbox: true,
    },
    defaultSettings: {
      paddingTop: 40,
      paddingBottom: 40,
    },
  },
  video: {
    type: 'video',
    name: 'Video',
    nameAr: 'فيديو',
    description: 'Embedded video from YouTube or Vimeo',
    descriptionAr: 'فيديو مضمن من يوتيوب أو فيميو',
    category: 'media',
    icon: 'video',
    defaultContent: {
      url: '',
      provider: 'youtube',
      controls: true,
    },
    defaultSettings: {
      paddingTop: 40,
      paddingBottom: 40,
    },
  },
  features: {
    type: 'features',
    name: 'Features',
    nameAr: 'الميزات',
    description: 'Highlight key features or benefits',
    descriptionAr: 'تسليط الضوء على الميزات الرئيسية',
    category: 'content',
    icon: 'list-checks',
    defaultContent: {
      title: 'Nos avantages',
      features: [
        { icon: 'check', title: 'Qualité', description: 'Service de haute qualité' },
        { icon: 'clock', title: 'Rapidité', description: 'Livraison rapide' },
        { icon: 'shield', title: 'Fiabilité', description: 'Garantie satisfaction' },
      ],
      columns: 3,
      style: 'cards',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  services: {
    type: 'services',
    name: 'Services',
    nameAr: 'الخدمات',
    description: 'Showcase your services',
    descriptionAr: 'عرض خدماتك',
    category: 'business',
    icon: 'briefcase',
    defaultContent: {
      title: 'Nos services',
      services: [
        { title: 'Service 1', description: 'Description du service', price: 'À partir de 50 TND' },
        { title: 'Service 2', description: 'Description du service', price: 'À partir de 100 TND' },
      ],
      columns: 3,
      style: 'cards',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  testimonials: {
    type: 'testimonials',
    name: 'Testimonials',
    nameAr: 'الشهادات',
    description: 'Customer testimonials and reviews',
    descriptionAr: 'شهادات ومراجعات العملاء',
    category: 'content',
    icon: 'quote',
    defaultContent: {
      title: 'Ce que disent nos clients',
      testimonials: [
        { content: 'Excellent service, je recommande!', author: 'Ahmed B.', rating: 5 },
        { content: 'Très professionnel et rapide.', author: 'Fatma M.', rating: 5 },
      ],
      style: 'cards',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
      backgroundColor: '#f8f9fa',
    },
  },
  team: {
    type: 'team',
    name: 'Team',
    nameAr: 'الفريق',
    description: 'Team members showcase',
    descriptionAr: 'عرض أعضاء الفريق',
    category: 'content',
    icon: 'users',
    defaultContent: {
      title: 'Notre équipe',
      members: [],
      columns: 4,
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  pricing: {
    type: 'pricing',
    name: 'Pricing',
    nameAr: 'التسعير',
    description: 'Pricing plans comparison',
    descriptionAr: 'مقارنة خطط التسعير',
    category: 'business',
    icon: 'credit-card',
    defaultContent: {
      title: 'Nos tarifs',
      plans: [
        {
          name: 'Basic',
          price: '49 TND',
          period: '/mois',
          features: ['Feature 1', 'Feature 2'],
          ctaText: 'Choisir',
          ctaLink: '/contact',
        },
        {
          name: 'Pro',
          price: '99 TND',
          period: '/mois',
          features: ['Feature 1', 'Feature 2', 'Feature 3'],
          ctaText: 'Choisir',
          ctaLink: '/contact',
          highlighted: true,
        },
      ],
      style: 'cards',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  faq: {
    type: 'faq',
    name: 'FAQ',
    nameAr: 'الأسئلة الشائعة',
    description: 'Frequently asked questions',
    descriptionAr: 'الأسئلة المتكررة',
    category: 'content',
    icon: 'help-circle',
    defaultContent: {
      title: 'Questions fréquentes',
      faqs: [
        { question: 'Question 1?', answer: 'Réponse à la question 1.' },
        { question: 'Question 2?', answer: 'Réponse à la question 2.' },
      ],
      style: 'accordion',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  contact: {
    type: 'contact',
    name: 'Contact',
    nameAr: 'اتصل بنا',
    description: 'Contact form and information',
    descriptionAr: 'نموذج الاتصال والمعلومات',
    category: 'forms',
    icon: 'mail',
    defaultContent: {
      title: 'Contactez-nous',
      showForm: true,
      formFields: [
        { name: 'name', label: 'Nom', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'email', required: true },
        { name: 'phone', label: 'Téléphone', type: 'phone', required: false },
        { name: 'message', label: 'Message', type: 'textarea', required: true },
      ],
      showInfo: true,
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  map: {
    type: 'map',
    name: 'Map',
    nameAr: 'خريطة',
    description: 'Google Maps location',
    descriptionAr: 'موقع على خرائط جوجل',
    category: 'content',
    icon: 'map-pin',
    defaultContent: {
      address: 'Tunis, Tunisie',
      zoom: 14,
      height: 400,
      showMarker: true,
    },
    defaultSettings: {
      paddingTop: 0,
      paddingBottom: 0,
      fullWidth: true,
    },
  },
  cta: {
    type: 'cta',
    name: 'Call to Action',
    nameAr: 'دعوة للعمل',
    description: 'Call-to-action section',
    descriptionAr: 'قسم دعوة للعمل',
    category: 'content',
    icon: 'megaphone',
    defaultContent: {
      title: 'Prêt à commencer?',
      description: 'Contactez-nous dès aujourd\'hui pour un devis gratuit.',
      buttons: [{ text: 'Contactez-nous', link: '/contact', variant: 'primary' }],
      style: 'centered',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
      backgroundColor: '#1a365d',
    },
  },
  footer: {
    type: 'footer',
    name: 'Footer',
    nameAr: 'التذييل',
    description: 'Page footer with links and info',
    descriptionAr: 'تذييل الصفحة مع الروابط والمعلومات',
    category: 'layout',
    icon: 'layout-navbar-expand',
    defaultContent: {
      description: 'Votre partenaire de confiance depuis 2024.',
      columns: [
        {
          title: 'Liens utiles',
          links: [
            { label: 'Accueil', url: '/' },
            { label: 'Services', url: '/services' },
            { label: 'Contact', url: '/contact' },
          ],
        },
      ],
      copyright: '© 2024 Mon Entreprise. Tous droits réservés.',
      style: 'simple',
    },
    defaultSettings: {
      backgroundColor: '#1a202c',
      paddingTop: 40,
      paddingBottom: 20,
    },
  },
  menu: {
    type: 'menu',
    name: 'Menu',
    nameAr: 'قائمة الطعام',
    description: 'Restaurant menu with categories',
    descriptionAr: 'قائمة المطعم مع الفئات',
    category: 'business',
    icon: 'utensils',
    defaultContent: {
      title: 'Notre Menu',
      categories: [
        {
          name: 'Entrées',
          items: [
            { name: 'Salade tunisienne', description: 'Salade fraîche', price: 8, available: true },
          ],
        },
      ],
      style: 'simple',
      showPrices: true,
      currency: 'TND',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  booking: {
    type: 'booking',
    name: 'Booking',
    nameAr: 'الحجز',
    description: 'Appointment booking form',
    descriptionAr: 'نموذج حجز المواعيد',
    category: 'forms',
    icon: 'calendar',
    defaultContent: {
      title: 'Réservez votre rendez-vous',
      services: [
        { name: 'Consultation', duration: 30, price: 50 },
      ],
      showCalendar: true,
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  products: {
    type: 'products',
    name: 'Products',
    nameAr: 'المنتجات',
    description: 'Product showcase grid',
    descriptionAr: 'شبكة عرض المنتجات',
    category: 'business',
    icon: 'shopping-bag',
    defaultContent: {
      title: 'Nos produits',
      products: [],
      columns: 4,
      showPrices: true,
      currency: 'TND',
      style: 'grid',
    },
    defaultSettings: {
      paddingTop: 60,
      paddingBottom: 60,
    },
  },
  hours: {
    type: 'hours',
    name: 'Business Hours',
    nameAr: 'ساعات العمل',
    description: 'Display business hours',
    descriptionAr: 'عرض ساعات العمل',
    category: 'business',
    icon: 'clock',
    defaultContent: {
      title: 'Horaires d\'ouverture',
      hours: {
        monday: { open: '09:00', close: '18:00' },
        tuesday: { open: '09:00', close: '18:00' },
        wednesday: { open: '09:00', close: '18:00' },
        thursday: { open: '09:00', close: '18:00' },
        friday: { open: '09:00', close: '18:00' },
        saturday: { open: '09:00', close: '13:00' },
        sunday: { open: '09:00', close: '13:00', closed: true },
      },
      showCurrentStatus: true,
      timezone: 'Africa/Tunis',
    },
    defaultSettings: {
      paddingTop: 40,
      paddingBottom: 40,
    },
  },
  social: {
    type: 'social',
    name: 'Social Feed',
    nameAr: 'تغذية اجتماعية',
    description: 'Display social media feed',
    descriptionAr: 'عرض تغذية وسائل التواصل الاجتماعي',
    category: 'media',
    icon: 'share-2',
    defaultContent: {
      platform: 'instagram',
      postsCount: 6,
      columns: 3,
    },
    defaultSettings: {
      paddingTop: 40,
      paddingBottom: 40,
    },
  },
  newsletter: {
    type: 'newsletter',
    name: 'Newsletter',
    nameAr: 'النشرة الإخبارية',
    description: 'Newsletter signup form',
    descriptionAr: 'نموذج الاشتراك في النشرة الإخبارية',
    category: 'forms',
    icon: 'mail-plus',
    defaultContent: {
      title: 'Restez informé',
      description: 'Inscrivez-vous à notre newsletter pour recevoir nos dernières actualités.',
      placeholder: 'Votre email',
      buttonText: 'S\'inscrire',
      successMessage: 'Merci pour votre inscription!',
    },
    defaultSettings: {
      paddingTop: 40,
      paddingBottom: 40,
      backgroundColor: '#f8f9fa',
    },
  },
  divider: {
    type: 'divider',
    name: 'Divider',
    nameAr: 'فاصل',
    description: 'Visual divider line',
    descriptionAr: 'خط فاصل مرئي',
    category: 'utility',
    icon: 'minus',
    defaultContent: {
      style: 'line',
      width: 'medium',
    },
    defaultSettings: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
  spacer: {
    type: 'spacer',
    name: 'Spacer',
    nameAr: 'مسافة',
    description: 'Empty space between blocks',
    descriptionAr: 'مسافة فارغة بين الكتل',
    category: 'utility',
    icon: 'move-vertical',
    defaultContent: {
      height: 60,
    },
    defaultSettings: {},
  },
  html: {
    type: 'html',
    name: 'Custom HTML',
    nameAr: 'HTML مخصص',
    description: 'Custom HTML code (premium)',
    descriptionAr: 'كود HTML مخصص (مميز)',
    category: 'utility',
    icon: 'code',
    premium: true,
    defaultContent: {
      code: '<!-- Your custom HTML here -->',
      sandboxed: true,
    },
    defaultSettings: {
      paddingTop: 20,
      paddingBottom: 20,
    },
  },
};

export function getBlockDefinition(type: BlockType): BlockDefinition | undefined {
  return blockRegistry[type];
}

export function getAllBlocks(): BlockDefinition[] {
  return Object.values(blockRegistry);
}

export function getBlocksByCategory(category: BlockCategory): BlockDefinition[] {
  return Object.values(blockRegistry).filter((block) => block.category === category);
}
